import type { DecklistInvalidDetails } from 'types/interfaces/play';

/**
 * Error codes from the Play backend (Contract delta, review-corrected spellings) and
 * the copy shown for each. Unknown codes fall back to the server message, then a generic line.
 */
export const PLAY_ERROR_MESSAGES: Record<string, string> = {
  ROUND_ALREADY_STARTED: 'The round has already started, so pairings are locked.',
  MATCH_ALREADY_REPORTED: 'The next round is already paired, so this result is final.',
  REGISTRATION_CLOSED: 'Registration for this tournament has closed.',
  ALREADY_JOINED: "You're already registered for this tournament.",
  USERNAME_TAKEN: 'That username is taken. Pick another.',
  INVALID_RESULT: "That result isn't allowed for this round.",
  NOT_IN_MATCH: "You're not in this match.",
  NOT_PARTICIPANT: "You're not registered for this tournament.",
  LATE_REGISTRATION_CLOSED: 'Registration for this tournament has closed.',
  INVITE_NOT_FOUND: "We couldn't find that tournament.",
  JUDGE_CALL_OPEN: 'A judge is already on the way to this table.',
  RESULTS_PENDING: 'Every match needs a result first.',
  ROUND_EXISTS: 'This round was already paired.',
  TOURNAMENT_COMPLETE: 'This tournament is over.',
  SAME_MATCH: 'Pick two players from different tables.',
  NOT_PAUSED: "The clock isn't paused.",
  ALREADY_PAUSED: 'The clock is already paused.',
  REINSTATE_WINDOW_CLOSED: 'The next round is paired; this player can no longer be reinstated.',
  DECKLIST_LOCKED: 'Locked when round 1 started. Ask a judge for changes.',
  DECKLIST_INVALID: "Something's off with this decklist.",
  ELITE_REQUIRED: 'Text alerts are an Elite perk.',
  PHONE_NOT_VERIFIED: 'Verify your phone number first to get texts.',
  NOT_ORGANIZER: 'Only the organizer can do that.',
  TOURNAMENT_NOT_FOUND: "We couldn't find that tournament.",
  DISCORD_SERVER_REQUIRED: 'Join the TradeMagic Discord server first so our bot can message you.',
  DISCORD_DENIED: 'Discord sign-in was cancelled.',
  DISCORD_EMAIL_REQUIRED: 'Your Discord account has no verified email. Use a TradeMagic account instead.',
  STATE_INVALID: 'That sign-in link expired. Try again from the invite page.',
  BANNED: 'This account is not allowed to sign in.',
  ACCOUNT_DELETED: 'This TradeMagic account was deleted, so Discord can no longer sign you in.',
  DISCORD_NOT_CONFIGURED: "Discord sign-in isn't available right now. Use your TradeMagic account instead.",
  DISCORD_ERROR: 'Discord had a problem signing you in. Try again.',
  // Ruling E10 (review's final names; the backend's earlier spellings kept as aliases)
  SERVER_JOIN_FAILED: 'Could not connect to the TradeMagic Discord server. Try again.',
  GUILD_JOIN_FAILED: 'Could not connect to the TradeMagic Discord server. Try again.',
  DISCORD_ACCOUNT_MISMATCH: 'That Discord account is not the one linked to this TradeMagic account.',
  DISCORD_MISMATCH: 'That Discord account is not the one linked to this TradeMagic account.',
  DISCORD_NOT_LINKED: 'Sign in with Discord once to turn on Discord alerts.',
};

/** Generic copy for a failed Discord server join (ruling E10). */
/** Discord DM opt-in failure (Eric's ruling 2026-10-30); rendered with a Try again button. */
export const DISCORD_JOIN_UNAVAILABLE = "Couldn't connect Discord right now. You'll still get round alerts here on TradeMagic.";

const GENERIC = 'Something went wrong. Please try again.';

/**
 * The backend's ErrorHandler shape is flat: { status: 'error', code, message, ...details },
 * so detail fields (mainCount, startUrl, ...) sit next to the code. A nested `details`
 * object is accepted too.
 */
interface ErrorBodyLike {
  code?: string;
  error?: string;
  message?: string;
  details?: unknown;
  [key: string]: unknown;
}

const detailsOf = (body: ErrorBodyLike): Record<string, unknown> => {
  if (body.details && typeof body.details === 'object') return body.details as Record<string, unknown>;
  return body;
};

const bodyOf = (err: unknown): ErrorBodyLike | null => {
  if (!err || typeof err !== 'object') return null;
  const data = (err as { data?: unknown }).data;
  if (data && typeof data === 'object') return data as ErrorBodyLike;
  return err as ErrorBodyLike;
};

/** The backend's error code, if the body carries one. */
export const playErrorCode = (err: unknown): string | null => {
  const body = bodyOf(err);
  if (!body) return null;
  const code = body.code ?? body.error;
  return typeof code === 'string' && /^[A-Z_]+$/.test(code) ? code : null;
};

export const playErrorMessage = (err: unknown, fallback: string = GENERIC): string => {
  const code = playErrorCode(err);
  if (code && PLAY_ERROR_MESSAGES[code]) return PLAY_ERROR_MESSAGES[code];
  const body = bodyOf(err);
  return typeof body?.message === 'string' && body.message.trim() ? body.message : fallback;
};

export const decklistInvalidDetails = (err: unknown): DecklistInvalidDetails | null => {
  if (playErrorCode(err) !== 'DECKLIST_INVALID') return null;
  const body = bodyOf(err);
  const d = (body ? detailsOf(body) : {}) as Partial<DecklistInvalidDetails>;
  return {
    mainCount: typeof d.mainCount === 'number' ? d.mainCount : 0,
    sideCount: typeof d.sideCount === 'number' ? d.sideCount : 0,
    unresolved: Array.isArray(d.unresolved) ? d.unresolved : [],
    issues: Array.isArray(d.issues) ? d.issues : [],
  };
};

/** 409 DISCORD_SERVER_REQUIRED carries the join-server start path (ruling E10). */
export const discordServerStartUrl = (err: unknown): string | null => {
  if (playErrorCode(err) !== 'DISCORD_SERVER_REQUIRED') return null;
  const body = bodyOf(err);
  const startUrl = body ? detailsOf(body).startUrl : null;
  return typeof startUrl === 'string' && startUrl.startsWith('/') ? startUrl : null;
};
